import PreviewReviewQueue from './PreviewReviewQueue';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PriceTestOperation, { type PriceTestOperationData } from './PriceTestOperation';
import AdminHeader from '../../components/AdminHeader';
import { useAdminRole } from '../../context/AdminRoleContext';

type PedidoStatus =
  | 'aguardando_pagamento'
  | 'pago'
  | 'gerando_musica'
  | 'entregando'
  | 'pronto_para_entrega'
  | 'entregue'
  | 'falha_suno'
  | 'falha_entrega'
  | 'cancelado'
  | 'expirado'
  | string;

type Pedido = {
  ref_id: string;
  cliente_phone: string;
  cliente_nome: string | null;
  nome_homenageado: string | null;
  relacao: string | null;
  status: PedidoStatus;
  valor: number | null;
  cupom_codigo: string | null;
  criado_em: string | null;
  atualizado_em: string | null;
  audio_url: string | null;
  audio_1_url?: string | null;
  audio_2_url?: string | null;
  video_url?: string | null;
};

type Atribuicao = {
  ad_id: string;
  ad_name: string | null;
  ad_headline: string | null;
  campaign_name: string | null;
};

type Conversation = {
  session_id: string;
  phone: string;
  phone_normalized: string;
  phone_display: string;
  last_message_id: number;
  last_message_at: string | null;
  last_message_preview: string;
  last_message_type: 'human' | 'ai' | string;
  message_count: number;
  pedido: Pedido | null;
  atribuicao: Atribuicao | null;
  paused?: boolean;
  paused_indefinite?: boolean;
  offer_id?: string | null;
  offer_label?: string | null;
  source?: string | null;
  closed?: boolean;
  full_delivery_label?: string | null;
  ctwa_clid?: string | null;
};

// Rótulo amigável do anúncio de origem: nome do anúncio (sincronizado do Meta) →
// headline do anúncio (texto que o cliente viu) → ad_id cru como último recurso.
function adLabel(at: Atribuicao | null): string {
  if (!at) return '';
  return (at.ad_name || at.ad_headline || at.ad_id || '').trim();
}

type MediaInfo = {
  kind: 'image' | 'audio' | 'video' | 'document';
  url: string;
  mime: string | null;
  caption: string | null;
  transcript: string | null;
  filename: string | null;
};

type ThreadMessage = {
  id: number;
  type: 'human' | 'ai' | string;
  content: string;
  media: MediaInfo | null;
  origem?: string | null;
  created_at: string | null;
};

type StatusFilter = 'all' | 'pendente' | 'sem_pedido' | 'pago' | 'entregue';
type HoursFilter = 1 | 6 | 24 | 168 | 'all';

type TakeoverState = {
  paused: boolean;
  paused_until: string | null;
  indefinite: boolean;
};

type PendingConversationPix = {
  pending: true;
  label: string;
  ref_id: string;
  pix: {
    id: number;
    source: 'pendente' | 'orfao' | string;
    valor: number;
    pagador_nome: string | null;
  };
};

type ComposeFile = {
  base64: string;
  mime: string;
  filename: string;
  size: number;
  preview: string | null;
};

type ComposeMediaType = 'image' | 'audio' | 'video' | 'document';

const REFRESH_MS = 30_000;
const TAKEOVER_REFRESH_MS = 20_000;
const ADMIN_PREFIX = '[admin] ';

const TYPE_LIMITS_BYTES: Record<ComposeMediaType, number> = {
  image: 5 * 1024 * 1024,
  audio: 16 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  document: 25 * 1024 * 1024,
};

function detectMediaType(mime: string): ComposeMediaType {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('audio/')) return 'audio';
  if (m.startsWith('video/')) return 'video';
  return 'document';
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

function base64ToBlob(base64: string, mime: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatHHMM(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

const STATUS_META: Record<string, { label: string; bg: string; fg: string; border: string }> = {
  aguardando_pagamento: { label: 'Aguardando pagamento', bg: 'rgba(255,176,80,0.18)', fg: '#cc6620', border: 'rgba(255,145,77,0.45)' },
  pago: { label: 'Pago', bg: 'rgba(80,160,220,0.18)', fg: '#1e6fa8', border: 'rgba(80,160,220,0.45)' },
  gerando_musica: { label: 'Gerando música', bg: 'rgba(80,160,220,0.18)', fg: '#1e6fa8', border: 'rgba(80,160,220,0.45)' },
  entregando: { label: 'Entregando', bg: 'rgba(80,160,220,0.18)', fg: '#1e6fa8', border: 'rgba(80,160,220,0.45)' },
  pronto_para_entrega: { label: 'Pronto p/ entrega', bg: 'rgba(80,160,220,0.18)', fg: '#1e6fa8', border: 'rgba(80,160,220,0.45)' },
  entregue: { label: 'Entregue', bg: 'rgba(95,180,120,0.20)', fg: '#2f7a4a', border: 'rgba(95,180,120,0.45)' },
  falha_suno: { label: 'Falha (Suno)', bg: 'rgba(220,80,80,0.18)', fg: '#a93232', border: 'rgba(220,80,80,0.45)' },
  falha_entrega: { label: 'Falha (entrega)', bg: 'rgba(220,80,80,0.18)', fg: '#a93232', border: 'rgba(220,80,80,0.45)' },
  falha_geracao_previa: { label: 'Falha (prévias)', bg: 'rgba(220,80,80,0.18)', fg: '#a93232', border: 'rgba(220,80,80,0.45)' },
  cancelado: { label: 'Cancelado', bg: 'rgba(140,140,150,0.18)', fg: '#5a5a66', border: 'rgba(140,140,150,0.45)' },
  expirado: { label: 'Expirado', bg: 'rgba(140,140,150,0.18)', fg: '#5a5a66', border: 'rgba(140,140,150,0.45)' },
  briefing: { label: 'Coletando', bg: 'rgba(80,160,220,0.14)', fg: '#1e6fa8', border: 'rgba(80,160,220,0.40)' },
  preview: { label: 'Prévia', bg: 'rgba(80,160,220,0.18)', fg: '#1e6fa8', border: 'rgba(80,160,220,0.45)' },
  collecting_photos: { label: 'Fotos', bg: 'rgba(80,160,220,0.18)', fg: '#1e6fa8', border: 'rgba(80,160,220,0.45)' },
};

function isPriceTestConversation(conv: Conversation | null | undefined): boolean {
  return Boolean(
    conv && (conv.source === 'price_test' || String(conv.session_id || '').startsWith('price-test:')),
  );
}

function OfferBadge(props: { conv: Conversation; compact?: boolean }) {
  const label = props.conv.offer_label;
  if (!label) return null;
  return (
    <span
      title="Oferta de preço 990/1990 — conversa fora do V16"
      style={{
        padding: props.compact ? '1px 7px' : '1px 8px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        background: 'rgba(255,108,108,0.14)',
        color: '#c44d4d',
        border: '1px solid rgba(255,108,108,0.40)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function formatRelative(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `há ${Math.floor(diff / 86400)}d`;
  return date.toLocaleDateString('pt-BR');
}

function digitsOnly(raw: string): string {
  return String(raw || '').replace(/\D/g, '');
}

function isLikelyHuman(content: string): boolean {
  return Boolean(content && content.trim().length > 0);
}

const ITEM_BTN_BASE: React.CSSProperties = {
  appearance: 'none',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  color: 'inherit',
};

export default function Conversas() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSessionId = searchParams.get('session');

  // Gestor de tráfego (viewer) vê as conversas mas não envia mensagem,
  // não pausa nem faz handoff da Isa.
  const readOnly = useAdminRole() !== 'admin';

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [priceOperation, setPriceOperation] = useState<PriceTestOperationData | null>(null);
  const [thread, setThread] = useState<ThreadMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState('');

  const [hoursFilter, setHoursFilter] = useState<HoursFilter>(24);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  const [composeText, setComposeText] = useState('');
  const [composeFile, setComposeFile] = useState<ComposeFile | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [takeover, setTakeover] = useState<TakeoverState>({ paused: false, paused_until: null, indefinite: false });
  const [pausing, setPausing] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [handingOff, setHandingOff] = useState(false);
  const [handoffMessage, setHandoffMessage] = useState('');
  const [pendingPix, setPendingPix] = useState<PendingConversationPix | null>(null);
  const [acceptingPix, setAcceptingPix] = useState(false);
  const [acceptPixError, setAcceptPixError] = useState('');

  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const threadScrollRef = useRef<HTMLDivElement | null>(null);
  const wasAtBottomRef = useRef<boolean>(true);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadList = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ mode: 'list' });
      if (hoursFilter === 'all') {
        qs.set('include_all', '1');
      } else {
        qs.set('hours', String(hoursFilter));
      }
      const res = await fetch(`/api/admin-conversas?${qs.toString()}`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setConversations(data.conversations || []);
      setListError('');
    } catch (err) {
      setListError(`Falha ao carregar conversas: ${(err as Error).message}`);
    } finally {
      setListLoading(false);
    }
  }, [hoursFilter]);

  useEffect(() => {
    loadList();
    const t = setInterval(loadList, REFRESH_MS);
    return () => clearInterval(t);
  }, [loadList]);

  const loadThread = useCallback(async (sessionId: string) => {
    try {
      const res = await fetch(`/api/admin-conversas?mode=thread&session_id=${encodeURIComponent(sessionId)}`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setThread(data.messages || []);
      setPriceOperation(data.operation || null);
      setThreadError('');
    } catch (err) {
      setThreadError(`Falha ao carregar conversa: ${(err as Error).message}`);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedSessionId) {
      setThread([]);
      return;
    }
    setThreadLoading(true);
    setPriceOperation(null);
    setThread([]);
    wasAtBottomRef.current = true;
    loadThread(selectedSessionId);
    const t = setInterval(() => {
      const sc = threadScrollRef.current;
      if (sc) {
        const distance = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
        wasAtBottomRef.current = distance < 80;
      }
      loadThread(selectedSessionId);
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [selectedSessionId, loadThread]);

  useEffect(() => {
    if (!thread.length) return;
    if (wasAtBottomRef.current) {
      threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [thread]);

  // Reset compose state ao trocar de conversa
  useEffect(() => {
    setComposeText('');
    setComposeFile(null);
    setSendError('');
    setTakeover({ paused: false, paused_until: null, indefinite: false });
    setPendingPix(null);
    setAcceptPixError('');
    setAcceptingPix(false);
  }, [selectedSessionId]);

  // Polling do estado de takeover
  const loadTakeover = useCallback(async (phone: string) => {
    if (!phone) return;
    try {
      const r = await fetch(`/api/admin-takeover?phone=${encodeURIComponent(phone)}`);
      if (!r.ok) return;
      const data = await r.json();
      setTakeover({ paused: !!data.paused, paused_until: data.paused_until || null, indefinite: !!data.indefinite });
    } catch {
      // soft-fail
    }
  }, []);

  const selectedPhone = useMemo(() => {
    if (!selectedSessionId) return '';
    const conv = conversations.find((c) => c.session_id === selectedSessionId);
    return conv ? digitsOnly(conv.phone) : '';
  }, [conversations, selectedSessionId]);

  useEffect(() => {
    if (!selectedPhone) return;
    loadTakeover(selectedPhone);
    const t = setInterval(() => loadTakeover(selectedPhone), TAKEOVER_REFRESH_MS);
    return () => clearInterval(t);
  }, [selectedPhone, loadTakeover]);

  const loadPendingPix = useCallback(async (sessionId: string) => {
    if (!sessionId || readOnly || String(sessionId).startsWith('price-test:')) {
      setPendingPix(null);
      return;
    }
    try {
      const r = await fetch(`/api/admin-pix?mode=conversa&session=${encodeURIComponent(sessionId)}`, {
        credentials: 'same-origin',
      });
      if (!r.ok) {
        setPendingPix(null);
        return;
      }
      const data = await r.json();
      if (data?.pending && data.label) {
        setPendingPix(data as PendingConversationPix);
      } else {
        setPendingPix(null);
      }
    } catch {
      setPendingPix(null);
    }
  }, [readOnly]);

  useEffect(() => {
    if (!selectedSessionId || readOnly) {
      setPendingPix(null);
      return;
    }
    loadPendingPix(selectedSessionId);
    const t = setInterval(() => loadPendingPix(selectedSessionId), REFRESH_MS);
    return () => clearInterval(t);
  }, [selectedSessionId, readOnly, loadPendingPix]);

  const acceptConversationPix = useCallback(async () => {
    if (!selectedSessionId || acceptingPix || readOnly) return;
    setAcceptingPix(true);
    setAcceptPixError('');
    try {
      const r = await fetch('/api/admin-pix?target=conversa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ session_id: selectedSessionId }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
      setPendingPix(null);
      await Promise.all([loadList(), loadThread(selectedSessionId)]);
    } catch (err) {
      setAcceptPixError(`Não consegui aceitar o Pix: ${(err as Error).message}`);
    } finally {
      setAcceptingPix(false);
    }
  }, [selectedSessionId, acceptingPix, readOnly, loadList, loadThread]);

  // Auto-clear do banner de pausa quando expira (pausa indefinida nunca expira sozinha)
  useEffect(() => {
    if (!takeover.paused || !takeover.paused_until || takeover.indefinite) return;
    const ms = new Date(takeover.paused_until).getTime() - Date.now();
    if (ms <= 0) {
      setTakeover({ paused: false, paused_until: null, indefinite: false });
      return;
    }
    const t = setTimeout(() => setTakeover({ paused: false, paused_until: null, indefinite: false }), ms + 1000);
    return () => clearTimeout(t);
  }, [takeover.paused, takeover.paused_until, takeover.indefinite]);

  const handleFileSelect = useCallback(async (file: File | null) => {
    setSendError('');
    if (!file) {
      setComposeFile(null);
      return;
    }
    const mime = file.type || 'application/octet-stream';
    const type = detectMediaType(mime);
    const limit = TYPE_LIMITS_BYTES[type];
    if (file.size > limit) {
      setSendError(`Arquivo muito grande. Máximo para ${type}: ${formatBytes(limit)}.`);
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      const isImage = type === 'image';
      const preview = isImage ? `data:${mime};base64,${base64}` : null;
      setComposeFile({ base64, mime, filename: file.name, size: file.size, preview });
    } catch (err) {
      setSendError(`Falha ao ler arquivo: ${(err as Error).message}`);
    }
  }, []);

  const handleSend = useCallback(async () => {
    if (sending) return;
    if (!selectedPhone) return;
    const trimmed = composeText.trim();
    const hasFile = !!composeFile;
    if (!hasFile && !trimmed) return;

    setSending(true);
    setSendError('');

    const mediaType: ComposeMediaType | 'text' = hasFile ? detectMediaType(composeFile!.mime) : 'text';
    const payload: Record<string, unknown> = {
      phone: selectedPhone,
      type: mediaType,
    };
    if (mediaType === 'text') {
      payload.text = trimmed;
    } else {
      // Upload direto pro Supabase via signed URL — bypassa o limite de 4.5MB do gateway Vercel.
      try {
        const r1 = await fetch('/api/admin-send-message?action=upload_url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: mediaType, mime: composeFile!.mime }),
        });
        const data1 = await r1.json().catch(() => ({}));
        if (!r1.ok) {
          const msg =
            data1?.error === 'unsupported_mime'
              ? 'Tipo de arquivo não suportado pelo WhatsApp.'
              : data1?.message || data1?.error || `Falha ao preparar upload (HTTP ${r1.status}).`;
          setSendError(msg);
          setSending(false);
          return;
        }

        const blob = base64ToBlob(composeFile!.base64, composeFile!.mime);
        const r2 = await fetch(data1.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': composeFile!.mime, 'x-upsert': 'true' },
          body: blob,
        });
        if (!r2.ok) {
          setSendError(`Falha no upload do arquivo (HTTP ${r2.status}).`);
          setSending(false);
          return;
        }

        payload.media_path = data1.path;
        payload.media_mime = composeFile!.mime;
        if (trimmed) payload.caption = trimmed;
        if (mediaType === 'document') payload.filename = composeFile!.filename;
      } catch (err) {
        setSendError(`Erro no upload: ${(err as Error).message}`);
        setSending(false);
        return;
      }
    }

    try {
      const r = await fetch('/api/admin-send-message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg =
          data?.message ||
          (data?.error === 'outside_24h_window'
            ? 'Cliente fora da janela de 24h.'
            : data?.error === 'file_too_large'
              ? 'Arquivo muito grande.'
              : data?.error || `Falha ao enviar (HTTP ${r.status}).`);
        setSendError(msg);
        setSending(false);
        return;
      }

      const previewContent =
        mediaType === 'text'
          ? trimmed
          : mediaType === 'image'
            ? trimmed ? `[imagem] ${trimmed}` : '[imagem]'
            : mediaType === 'document'
              ? `[documento] ${composeFile!.filename}`
              : mediaType === 'audio'
                ? '[audio]'
                : trimmed ? `[video] ${trimmed}` : '[video]';

      const optimisticMedia: MediaInfo | null =
        mediaType === 'text'
          ? null
          : {
              kind: mediaType,
              url: composeFile!.preview || URL.createObjectURL(base64ToBlob(composeFile!.base64, composeFile!.mime)),
              mime: composeFile!.mime,
              caption: trimmed || null,
              transcript: null,
              filename: mediaType === 'document' ? composeFile!.filename : null,
            };

      setThread((prev) => [
        ...prev,
        {
          id: -Date.now(),
          type: 'ai',
          content: ADMIN_PREFIX + previewContent,
          media: optimisticMedia,
          created_at: new Date().toISOString(),
        },
      ]);
      setComposeText('');
      setComposeFile(null);
      if (data?.paused_until) {
        setTakeover((prev) => (prev.indefinite
          ? prev
          : { paused: true, paused_until: data.paused_until, indefinite: false }));
      }
      wasAtBottomRef.current = true;
      setTimeout(() => {
        if (selectedSessionId) loadThread(selectedSessionId);
      }, 1500);
    } catch (err) {
      setSendError(`Erro: ${(err as Error).message}`);
    } finally {
      setSending(false);
    }
  }, [sending, selectedPhone, composeText, composeFile, selectedSessionId, loadThread]);

  const releaseIsa = useCallback(async () => {
    if (!selectedPhone || releasing) return;
    setReleasing(true);
    setHandoffMessage('');
    try {
      const r = await fetch(`/api/admin-takeover?phone=${encodeURIComponent(selectedPhone)}`, {
        method: 'DELETE',
      });
      if (r.ok) {
        setTakeover({ paused: false, paused_until: null, indefinite: false });
      }
    } catch {
      // ignore
    } finally {
      setReleasing(false);
    }
  }, [selectedPhone, releasing]);

  const pauseIndefinite = useCallback(async () => {
    if (!selectedPhone || pausing) return;
    setPausing(true);
    setHandoffMessage('');
    try {
      const r = await fetch('/api/admin-takeover?action=pause', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: selectedPhone }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setTakeover({ paused: true, paused_until: data.paused_until || null, indefinite: true });
      }
    } catch {
      // ignore
    } finally {
      setPausing(false);
    }
  }, [selectedPhone, pausing]);

  const handoffToIsa = useCallback(async () => {
    if (!selectedPhone || handingOff) return;
    setHandingOff(true);
    setHandoffMessage('');
    try {
      const conv = conversations.find((c) => c.session_id === selectedSessionId);
      const r = await fetch('/api/admin-takeover?action=handoff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: selectedPhone,
          session_id: selectedSessionId,
          contactName: conv?.pedido?.cliente_nome || 'Cliente',
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setHandoffMessage(data?.error || `Falha ao passar pra Isa (HTTP ${r.status}).`);
        return;
      }
      setTakeover({ paused: false, paused_until: null, indefinite: false });
      setHandoffMessage('Atendimento liberado. Mensagens recebidas durante a pausa não serão enviadas agora; a Isa responderá a novas mensagens do cliente.');
      if (selectedSessionId) {
        setTimeout(() => loadThread(selectedSessionId), 4000);
        setTimeout(() => loadThread(selectedSessionId), 12000);
      }
    } catch (err) {
      setHandoffMessage(`Erro: ${(err as Error).message}`);
    } finally {
      setHandingOff(false);
    }
  }, [selectedPhone, handingOff, conversations, selectedSessionId, loadThread]);

  const filteredConversations = useMemo(() => {
    const cutoff = hoursFilter === 'all' ? -Infinity : Date.now() - hoursFilter * 3600 * 1000;
    const searchDigits = digitsOnly(search);
    const searchLower = search.trim().toLowerCase();

    return conversations.filter((c) => {
      if (statusFilter === 'pendente' && c.pedido?.status !== 'aguardando_pagamento') return false;
      if (statusFilter === 'sem_pedido' && c.pedido) return false;
      if (statusFilter === 'pago' && c.pedido?.status !== 'pago') return false;
      if (statusFilter === 'entregue' && c.pedido?.status !== 'entregue') return false;

      const rawTs = c.last_message_at || c.pedido?.atualizado_em || null;
      if (rawTs && cutoff !== -Infinity) {
        const t = new Date(rawTs).getTime();
        if (!isNaN(t) && t < cutoff) return false;
      }

      if (search.trim()) {
        const phoneMatch = searchDigits && c.phone_normalized.includes(searchDigits);
        const nameMatch =
          searchLower &&
          [c.pedido?.cliente_nome, c.pedido?.nome_homenageado, c.offer_label]
            .filter(Boolean)
            .some((n) => (n as string).toLowerCase().includes(searchLower));
        if (!phoneMatch && !nameMatch) return false;
      }
      return true;
    });
  }, [conversations, statusFilter, search, hoursFilter]);

  const selectedConv = useMemo(
    () => conversations.find((c) => c.session_id === selectedSessionId) || null,
    [conversations, selectedSessionId]
  );

  const selectConversation = (sessionId: string | null) => {
    if (sessionId) {
      setSearchParams({ session: sessionId });
    } else {
      setSearchParams({});
    }
  };

  // ----- Render -----

  const showList = !isMobile || !selectedSessionId;
  const showThread = !isMobile || !!selectedSessionId;

  return (
    <div
      className="admin-shell"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--cream)',
        color: 'var(--text-body)',
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <AdminHeader title="Conversas" />
      <PreviewReviewQueue />

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {showList && (
          <aside
            style={{
              width: isMobile ? '100%' : 360,
              borderRight: isMobile ? 'none' : '1px solid var(--border-soft)',
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--surface-white)',
              minHeight: 0,
            }}
          >
            <ListFilters
              hoursFilter={hoursFilter}
              setHoursFilter={setHoursFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              search={search}
              setSearch={setSearch}
            />

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                background: 'var(--cream)',
              }}
            >
              {listLoading && conversations.length === 0 && <ListSkeleton />}

              {listError && (
                <div style={{ padding: 20, textAlign: 'center' }}>
                  <p style={{ color: '#a93232', fontSize: 14, margin: '0 0 12px' }}>{listError}</p>
                  <button
                    onClick={() => {
                      setListLoading(true);
                      loadList();
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 999,
                      background: 'var(--gold)',
                      color: 'var(--gold-ink)',
                      border: 'none',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Tentar novamente
                  </button>
                </div>
              )}

              {!listLoading && !listError && filteredConversations.length === 0 && (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                  <p style={{ margin: 0, fontSize: 14 }}>Nenhuma conversa encontrada com esses filtros.</p>
                </div>
              )}

              {filteredConversations.map((c) => (
                <ConversationItem
                  key={c.session_id}
                  conv={c}
                  selected={c.session_id === selectedSessionId}
                  onClick={() => selectConversation(c.session_id)}
                />
              ))}
            </div>
          </aside>
        )}

        {showThread && (
          <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
            {!selectedConv && !threadLoading && (
              <EmptyThread />
            )}

            {selectedConv && (
              <>
                <ThreadHeader
                  conv={selectedConv}
                  isMobile={isMobile}
                  onBack={() => selectConversation(null)}
                  paused={takeover.paused}
                  pausing={pausing}
                  releasing={releasing}
                  onPause={pauseIndefinite}
                  onRelease={releaseIsa}
                  readOnly={readOnly}
                  pendingPix={pendingPix}
                  acceptingPix={acceptingPix}
                  acceptPixError={acceptPixError}
                  onAcceptPix={acceptConversationPix}
                />

                {takeover.paused && (
                  <TakeoverBanner
                    pausedUntil={takeover.paused_until}
                    indefinite={takeover.indefinite}
                    releasing={releasing}
                    handingOff={handingOff}
                    handoffMessage={handoffMessage}
                    onRelease={releaseIsa}
                    onHandoff={handoffToIsa}
                    readOnly={readOnly}
                    allowHandoff={!isPriceTestConversation(selectedConv)}
                  />
                )}

                {threadError && (
                  <div style={{ padding: 20, textAlign: 'center' }}>
                    <p style={{ color: '#a93232', fontSize: 14, margin: '0 0 12px' }}>{threadError}</p>
                    <button
                      onClick={() => selectedSessionId && loadThread(selectedSessionId)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 999,
                        background: 'var(--gold)',
                        color: 'var(--gold-ink)',
                        border: 'none',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Tentar novamente
                    </button>
                  </div>
                )}

                <div
                  ref={threadScrollRef}
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '20px 16px',
                    background: 'var(--cream)',
                  }}
                >
                  {threadLoading && thread.length === 0 && (
                    <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
                      Carregando conversa…
                    </div>
                  )}

                  <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {thread.map((msg) => (
                      <Bubble key={msg.id} msg={msg} />
                    ))}
                    <div ref={threadEndRef} />
                  </div>
                </div>

                {isPriceTestConversation(selectedConv) && (
                  <PriceTestOperation key={selectedSessionId} operation={priceOperation?.session_id === selectedSessionId ? priceOperation : null} phone={selectedPhone || ''} readOnly={readOnly} refresh={() => selectedSessionId && loadThread(selectedSessionId)} />
                )}
                {!readOnly && !isPriceTestConversation(selectedConv) && (
                  <Composer
                    text={composeText}
                    setText={setComposeText}
                    file={composeFile}
                    onFileSelect={handleFileSelect}
                    sending={sending}
                    error={sendError}
                    onSend={handleSend}
                  />
                )}
              </>
            )}
          </main>
        )}
      </div>
    </div>
  );
}

// ----- Sub-componentes -----

function ListFilters(props: {
  hoursFilter: HoursFilter;
  setHoursFilter: (h: HoursFilter) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (s: StatusFilter) => void;
  search: string;
  setSearch: (s: string) => void;
}) {
  const { hoursFilter, setHoursFilter, statusFilter, setStatusFilter, search, setSearch } = props;

  const hoursOpts: { value: HoursFilter; label: string }[] = [
    { value: 1, label: '1h' },
    { value: 6, label: '6h' },
    { value: 24, label: '24h' },
    { value: 168, label: '7d' },
    { value: 'all', label: 'Tudo' },
  ];

  const statusOpts: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'Todos' },
    { value: 'pendente', label: 'Pendente' },
    { value: 'sem_pedido', label: 'Sem pedido' },
    { value: 'pago', label: 'Pago' },
    { value: 'entregue', label: 'Entregue' },
  ];

  return (
    <div
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-soft)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        flexShrink: 0,
      }}
    >
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar por telefone ou nome…"
        style={{
          width: '100%',
          padding: '10px 14px',
          borderRadius: 999,
          border: '1px solid var(--border-soft)',
          background: 'var(--cream)',
          color: 'var(--text-body)',
          fontFamily: 'inherit',
          fontSize: 14,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />

      <div style={{ display: 'flex', gap: 4, overflowX: 'auto' }}>
        {hoursOpts.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setHoursFilter(opt.value)}
            style={{
              ...ITEM_BTN_BASE,
              padding: '6px 12px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: hoursFilter === opt.value ? 'var(--gold)' : 'var(--badge-bg)',
              color: hoursFilter === opt.value ? 'var(--gold-ink)' : 'var(--text-strong)',
              border: '1px solid',
              borderColor: hoursFilter === opt.value ? 'var(--gold)' : 'var(--border-soft)',
              flexShrink: 0,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 4, overflowX: 'auto' }}>
        {statusOpts.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setStatusFilter(opt.value)}
            style={{
              ...ITEM_BTN_BASE,
              padding: '6px 12px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: statusFilter === opt.value ? 'var(--earth)' : 'var(--badge-bg)',
              color: statusFilter === opt.value ? 'var(--cream)' : 'var(--text-strong)',
              border: '1px solid',
              borderColor: statusFilter === opt.value ? 'var(--earth)' : 'var(--border-soft)',
              flexShrink: 0,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

    </div>
  );
}

function ConversationItem(props: { conv: Conversation; selected: boolean; onClick: () => void }) {
  const { conv, selected, onClick } = props;
  const meta = conv.pedido ? STATUS_META[conv.pedido.status] : null;
  const lastIsCustomer = conv.last_message_type === 'human';
  const waiting = lastIsCustomer && isLikelyHuman(conv.last_message_preview);
  const displayName = conv.pedido?.cliente_nome || conv.pedido?.nome_homenageado || conv.phone_display;

  return (
    <button
      onClick={onClick}
      style={{
        ...ITEM_BTN_BASE,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        width: '100%',
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-fade)',
        background: selected ? 'var(--badge-bg)' : 'transparent',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, width: '100%' }}>
        <span
          style={{
            fontWeight: 700,
            fontSize: 14,
            color: 'var(--heading-ink)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
          }}
        >
          {displayName}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {conv.last_message_at
            ? formatRelative(conv.last_message_at)
            : conv.pedido?.atualizado_em
              ? formatRelative(conv.pedido.atualizado_em)
              : '—'}
        </span>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ whiteSpace: 'nowrap' }}>{conv.phone_display}</span>
        <OfferBadge conv={conv} compact />
        {conv.full_delivery_label && (
          <span
            title={conv.full_delivery_label}
            style={{
              padding: '1px 8px',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              background: 'rgba(95,180,120,0.18)',
              color: '#2f7a4a',
              border: '1px solid rgba(95,180,120,0.45)',
              whiteSpace: 'nowrap',
            }}
          >
            {conv.full_delivery_label}
          </span>
        )}
        {conv.paused && (
          <span
            title="Isa pausada — esta conversa não recebe resposta automática"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '1px 8px',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              background: 'rgba(220,80,80,0.18)',
              color: '#c62828',
              border: '1px solid rgba(220,80,80,0.55)',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#e03131' }} />
            Pausada
          </span>
        )}
        {meta ? (
          <span
            style={{
              padding: '1px 8px',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              background: meta.bg,
              color: meta.fg,
              border: `1px solid ${meta.border}`,
              whiteSpace: 'nowrap',
            }}
          >
            {meta.label}
          </span>
        ) : (
          <span
            style={{
              padding: '1px 8px',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              background: 'rgba(140,140,150,0.12)',
              color: 'var(--text-muted)',
              border: '1px solid var(--border-soft)',
              whiteSpace: 'nowrap',
            }}
          >
            Sem pedido
          </span>
        )}
        {waiting && (
          <span
            title="Última mensagem é do cliente"
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'var(--gold)',
              flexShrink: 0,
              marginLeft: 'auto',
            }}
          />
        )}
      </div>

      <div
        style={{
          fontSize: 13,
          color: 'var(--text-body)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%',
          opacity: 0.85,
        }}
      >
        {lastIsCustomer ? '' : '↩ '}
        {conv.last_message_preview || <em style={{ opacity: 0.5 }}>(sem prévia)</em>}
      </div>
    </button>
  );
}

function ThreadHeader(props: {
  conv: Conversation;
  isMobile: boolean;
  onBack: () => void;
  paused: boolean;
  pausing: boolean;
  releasing: boolean;
  onPause: () => void;
  onRelease: () => void;
  readOnly: boolean;
  pendingPix: PendingConversationPix | null;
  acceptingPix: boolean;
  acceptPixError: string;
  onAcceptPix: () => void;
}) {
  const {
    conv, isMobile, onBack, paused, pausing, releasing, onPause, onRelease, readOnly,
    pendingPix, acceptingPix, acceptPixError, onAcceptPix,
  } = props;
  const meta = conv.pedido ? STATUS_META[conv.pedido.status] : null;
  // A RPC já retorna os masters da mesma rodada de prévias, antes do pagamento.
  const hasVariantAudios = Boolean(conv.pedido?.audio_1_url || conv.pedido?.audio_2_url);

  return (
    <div
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-soft)',
        background: 'var(--surface-white)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {isMobile && (
          <button
            onClick={onBack}
            aria-label="Voltar"
            style={{
              ...ITEM_BTN_BASE,
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'var(--badge-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              color: 'var(--text-strong)',
            }}
          >
            ←
          </button>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 800,
              fontSize: 16,
              color: 'var(--heading-ink)',
              letterSpacing: '-0.02em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {conv.pedido?.cliente_nome || conv.phone_display}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {conv.phone_display} · {conv.message_count} mensagens
            {isPriceTestConversation(conv) ? ' · oferta 990/1990' : ''}
          </div>
          {conv.offer_label && (
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <OfferBadge conv={conv} />
              {conv.full_delivery_label && (
                <span
                  title={conv.full_delivery_label}
                  style={{
                    padding: '1px 8px',
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 700,
                    background: 'rgba(95,180,120,0.18)',
                    color: '#2f7a4a',
                    border: '1px solid rgba(95,180,120,0.45)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {conv.full_delivery_label}
                </span>
              )}
            </div>
          )}
          {conv.atribuicao && (
            <div
              title={`Cliente veio do anúncio${conv.atribuicao.campaign_name ? ` · campanha: ${conv.atribuicao.campaign_name}` : ''} (ad_id ${conv.atribuicao.ad_id})`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                marginTop: 5,
                maxWidth: '100%',
                padding: '2px 9px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                background: 'rgba(80,160,220,0.14)',
                color: '#1e6fa8',
                border: '1px solid rgba(80,160,220,0.40)',
              }}
            >
              <span style={{ flexShrink: 0 }}>📣</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {adLabel(conv.atribuicao)}
              </span>
            </div>
          )}
        </div>

        {!readOnly && (paused ? (
          <button
            onClick={onRelease}
            disabled={releasing}
            title="Reativa a Isa nesta conversa — ela volta a responder automaticamente."
            style={{
              ...ITEM_BTN_BASE,
              padding: '8px 14px',
              borderRadius: 999,
              background: '#2f7a4a',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: releasing ? 'wait' : 'pointer',
              opacity: releasing ? 0.6 : 1,
            }}
          >
            {releasing ? 'Reativando…' : '🔊 Reativar Isa'}
          </button>
        ) : (
          <button
            onClick={onPause}
            disabled={pausing}
            title="Silencia a Isa nesta conversa por tempo indefinido (atendimento manual ou cliente indesejado). Você reativa quando quiser."
            style={{
              ...ITEM_BTN_BASE,
              padding: '8px 14px',
              borderRadius: 999,
              background: '#d33b3b',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: pausing ? 'wait' : 'pointer',
              opacity: pausing ? 0.6 : 1,
              boxShadow: pausing ? 'none' : '0 2px 10px rgba(211,59,59,0.35)',
            }}
          >
            {pausing ? 'Pausando…' : '⏸ Pausar'}
          </button>
        ))}
      </div>

      {conv.pedido && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 8,
            padding: 12,
            borderRadius: 12,
            background: 'var(--badge-bg)',
            border: '1px solid var(--border-soft)',
          }}
        >
          <PedidoField label={isPriceTestConversation(conv) ? 'Oferta' : 'Pedido'} value={conv.pedido.ref_id} />
          {meta && (
            <PedidoField
              label="Status"
              value={
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    background: meta.bg,
                    color: meta.fg,
                    border: `1px solid ${meta.border}`,
                  }}
                >
                  {meta.label}
                </span>
              }
            />
          )}
          {conv.pedido.nome_homenageado && (
            <PedidoField label="Para" value={`${conv.pedido.nome_homenageado}${conv.pedido.relacao ? ` (${conv.pedido.relacao})` : ''}`} />
          )}
          {conv.pedido.valor != null && (
            <PedidoField label="Valor" value={`R$ ${Number(conv.pedido.valor).toFixed(2).replace('.', ',')}`} />
          )}
          {conv.ctwa_clid && (
            <PedidoField label="CTWA" value={conv.ctwa_clid} />
          )}
          {conv.pedido.cupom_codigo && <PedidoField label="Cupom" value={conv.pedido.cupom_codigo} />}
          {conv.pedido.criado_em && <PedidoField label="Criado" value={formatRelative(conv.pedido.criado_em)} />}
          {hasVariantAudios && (
            <PedidoField
              label="Músicas completas"
              value={
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {conv.pedido.audio_1_url && (
                    <a href={conv.pedido.audio_1_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)', fontWeight: 600 }}>
                      Música completa A →
                    </a>
                  )}
                  {conv.pedido.audio_2_url && (
                    <a href={conv.pedido.audio_2_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)', fontWeight: 600 }}>
                      Música completa B →
                    </a>
                  )}
                </span>
              }
            />
          )}
          {!hasVariantAudios && conv.pedido.audio_url && (
            <PedidoField
              label="Música"
              value={
                <a href={conv.pedido.audio_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)', fontWeight: 600 }}>
                  Abrir áudio →
                </a>
              }
            />
          )}
          {conv.pedido.video_url && (
            <PedidoField
              label="Vídeo"
              value={
                <a href={conv.pedido.video_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)', fontWeight: 600 }}>
                  Abrir vídeo →
                </a>
              }
            />
          )}
        </div>
      )}

      {!readOnly && pendingPix && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            borderRadius: 12,
            background: 'rgba(47,122,74,0.10)',
            border: '1px solid rgba(47,122,74,0.28)',
          }}
        >
          <div style={{ flex: '1 1 180px', minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#2f7a4a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Pix pendente nesta conversa
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-strong)', fontWeight: 600, marginTop: 2 }}>
              {pendingPix.pix.pagador_nome || 'Pagador não identificado'}
              {pendingPix.pix.valor != null ? ` · R$ ${Number(pendingPix.pix.valor).toFixed(2).replace('.', ',')}` : ''}
            </div>
            {acceptPixError && (
              <div style={{ fontSize: 12, color: '#a93232', marginTop: 4 }}>{acceptPixError}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onAcceptPix}
            disabled={acceptingPix}
            aria-label={pendingPix.label}
            style={{
              ...ITEM_BTN_BASE,
              padding: '8px 14px',
              borderRadius: 999,
              background: 'var(--gold)',
              color: 'var(--gold-ink)',
              fontSize: 13,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              cursor: acceptingPix ? 'wait' : 'pointer',
              opacity: acceptingPix ? 0.6 : 1,
            }}
          >
            {acceptingPix ? 'Aceitando…' : pendingPix.label}
          </button>
        </div>
      )}
    </div>
  );
}

function PedidoField(props: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 2,
        }}
      >
        {props.label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--text-strong)',
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {props.value}
      </div>
    </div>
  );
}

function formatBubbleTime(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function MediaContent(props: { media: MediaInfo; align: 'left' | 'right' }) {
  const { media, align } = props;
  const radius = 10;
  const captionOrFallback = media.caption || media.transcript || '';

  if (media.kind === 'image') {
    return (
      <div style={{ marginBottom: captionOrFallback ? 6 : 0 }}>
        <a href={media.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
          <img
            src={media.url}
            alt={media.caption || 'imagem'}
            style={{
              maxWidth: '100%',
              maxHeight: 280,
              borderRadius: radius,
              display: 'block',
              objectFit: 'cover',
            }}
          />
        </a>
      </div>
    );
  }

  if (media.kind === 'audio') {
    return (
      <div style={{ marginBottom: media.transcript ? 6 : 0 }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '2px 8px',
            borderRadius: 999,
            background: 'rgba(80,160,220,0.18)',
            color: '#1e6fa8',
            border: '1px solid rgba(80,160,220,0.45)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          🎤 Áudio
        </div>
        <audio
          controls
          src={media.url}
          style={{ width: '100%', maxWidth: 320, display: 'block', height: 36 }}
        />
      </div>
    );
  }

  if (media.kind === 'video') {
    return (
      <div style={{ marginBottom: captionOrFallback ? 6 : 0 }}>
        <video
          controls
          src={media.url}
          style={{
            maxWidth: '100%',
            maxHeight: 320,
            borderRadius: radius,
            display: 'block',
            background: '#000',
          }}
        />
      </div>
    );
  }

  // document
  return (
    <a
      href={media.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 10,
        background: 'rgba(95,90,180,0.10)',
        border: '1px solid var(--border-soft)',
        color: 'var(--text-strong)',
        textDecoration: 'none',
        marginBottom: captionOrFallback ? 6 : 0,
        fontSize: 13,
        fontWeight: 600,
        textAlign: align === 'right' ? 'right' : 'left',
        maxWidth: '100%',
      }}
    >
      <span style={{ fontSize: 18 }}>📎</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {media.filename || 'documento'}
      </span>
    </a>
  );
}

function Bubble(props: { msg: ThreadMessage }) {
  const { msg } = props;
  const isAi = msg.type === 'ai';
  const isHuman = msg.type === 'human';
  const isAdmin = isAi && typeof msg.content === 'string' && msg.content.startsWith(ADMIN_PREFIX);
  const isSite = isAi && msg.origem === 'site';
  const rawContent = isAdmin ? msg.content.slice(ADMIN_PREFIX.length) : msg.content;
  const time = formatBubbleTime(msg.created_at);
  const media = msg.media;

  // For audio messages, content is the transcription (or empty). For image/video, content
  // is the caption (or "[imagem]"/"[video]" fallback). Strip those bracket placeholders so
  // we don't show "[imagem]" alongside the actual image.
  const displayContent = (() => {
    if (!media) return rawContent;
    const trimmed = rawContent.trim();
    if (!trimmed) return '';
    if (/^\[(imagem|audio|áudio|video|documento)\]$/i.test(trimmed)) return '';
    if (media.kind === 'audio') {
      // Show the transcript (preferring explicit transcript field if set)
      return media.transcript || trimmed;
    }
    // Image/video/document: show caption only (already in content for outbound, or as caption field)
    return media.caption && media.caption !== trimmed ? media.caption : trimmed;
  })();

  const bg = isAdmin
    ? 'rgba(95,180,120,0.18)'
    : isSite
      ? 'rgba(80,160,220,0.14)'
      : isAi
        ? 'var(--gold-pale)'
        : 'var(--surface-white)';
  const borderColor = isAdmin
    ? 'rgba(95,180,120,0.45)'
    : isSite
      ? 'rgba(80,160,220,0.45)'
      : isAi
        ? 'var(--gold-border)'
        : 'var(--border-soft)';

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isAi ? 'flex-end' : isHuman ? 'flex-start' : 'center',
      }}
    >
      <div
        style={{
          maxWidth: '78%',
          padding: media?.kind === 'image' || media?.kind === 'video' ? '6px 6px 10px' : '10px 14px',
          borderRadius: 16,
          borderTopLeftRadius: isHuman ? 4 : 16,
          borderTopRightRadius: isAi ? 4 : 16,
          background: bg,
          border: `1px solid ${borderColor}`,
          color: 'var(--text-body)',
          fontSize: 14,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {isAdmin && (
          <div
            style={{
              fontSize: 10,
              color: '#2f7a4a',
              marginBottom: 4,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              padding: media?.kind === 'image' || media?.kind === 'video' ? '0 8px' : 0,
            }}
          >
            via admin
          </div>
        )}
        {isSite && (
          <div
            style={{
              fontSize: 10,
              color: '#1e6fa8',
              marginBottom: 4,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              padding: media?.kind === 'image' || media?.kind === 'video' ? '0 8px' : 0,
            }}
          >
            🌐 via site
          </div>
        )}
        {!isHuman && !isAi && (
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 700 }}>
            {msg.type.toUpperCase()}
          </div>
        )}
        {media && <MediaContent media={media} align={isAi ? 'right' : 'left'} />}
        {displayContent && (
          <div
            style={{
              padding: media?.kind === 'image' || media?.kind === 'video' ? '0 8px' : 0,
            }}
          >
            {displayContent}
          </div>
        )}
        {!media && !displayContent && <em style={{ opacity: 0.5 }}>(mensagem vazia)</em>}
        {time && (
          <div
            style={{
              fontSize: 10,
              color: 'var(--text-soft)',
              marginTop: 4,
              textAlign: isAi ? 'right' : 'left',
              fontWeight: 500,
              padding: media?.kind === 'image' || media?.kind === 'video' ? '0 8px' : 0,
            }}
          >
            {time}
          </div>
        )}
      </div>
    </div>
  );
}

function TakeoverBanner(props: {
  pausedUntil: string | null;
  indefinite: boolean;
  releasing: boolean;
  handingOff: boolean;
  handoffMessage: string;
  onRelease: () => void;
  onHandoff: () => void;
  readOnly: boolean;
  allowHandoff?: boolean;
}) {
  const { pausedUntil, indefinite, releasing, handingOff, handoffMessage, onRelease, onHandoff, readOnly, allowHandoff = true } = props;
  const until = formatHHMM(pausedUntil);
  const busy = releasing || handingOff;
  return (
    <div
      style={{
        padding: '10px 16px',
        background: 'rgba(95,180,120,0.14)',
        borderBottom: '1px solid rgba(95,180,120,0.35)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 18 }}>{indefinite ? '🔇' : '🤐'}</span>
        <div style={{ flex: 1, fontSize: 13, color: 'var(--text-strong)' }}>
          {indefinite ? (
            <>
              <strong>Atendimento pausado por tempo indefinido.</strong> A Isa fica em
              silêncio nesta conversa até você liberar.
            </>
          ) : (
            <>
              <strong>Você assumiu a conversa.</strong> A Isa está em silêncio
              {until ? ` até as ${until}` : ''}.
            </>
          )}
        </div>
        {!readOnly && (
          <>
        {allowHandoff && (
        <button
          onClick={onHandoff}
          disabled={busy}
          title="Encerra a pausa e a Isa olha o histórico, escreve uma resposta e manda agora"
          style={{
            ...ITEM_BTN_BASE,
            padding: '6px 12px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            background: 'var(--gold)',
            color: 'var(--gold-ink)',
            opacity: busy ? 0.6 : 1,
            cursor: busy ? 'wait' : 'pointer',
            boxShadow: busy ? 'none' : '0 2px 10px rgba(255,108,108,0.35)',
            whiteSpace: 'nowrap',
          }}
        >
          {handingOff ? 'Passando…' : '🚀 Passar pra Isa responder'}
        </button>
        )}
        <button
          onClick={onRelease}
          disabled={busy}
          title="Apenas encerra a pausa. A Isa só vai responder quando o cliente mandar próxima mensagem"
          style={{
            ...ITEM_BTN_BASE,
            padding: '6px 12px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            background: '#2f7a4a',
            color: '#fff',
            opacity: busy ? 0.6 : 1,
            cursor: busy ? 'wait' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {releasing ? 'Liberando…' : '🤖 Apenas liberar'}
        </button>
          </>
        )}
      </div>
      {handoffMessage && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-strong)',
            background: 'rgba(255,108,108,0.10)',
            border: '1px solid rgba(255,108,108,0.30)',
            padding: '6px 10px',
            borderRadius: 8,
          }}
        >
          {handoffMessage}
        </div>
      )}
    </div>
  );
}

function Composer(props: {
  text: string;
  setText: (v: string) => void;
  file: ComposeFile | null;
  onFileSelect: (file: File | null) => void;
  sending: boolean;
  error: string;
  onSend: () => void;
}) {
  const { text, setText, file, onFileSelect, sending, error, onSend } = props;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [text]);

  const fileType: ComposeMediaType | null = file ? detectMediaType(file.mime) : null;
  const canSend = !sending && (text.trim().length > 0 || !!file);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) onSend();
    }
  };

  return (
    <div
      style={{
        borderTop: '1px solid var(--border-soft)',
        background: 'var(--surface-white)',
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flexShrink: 0,
      }}
    >
      {file && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '6px 10px',
            background: 'var(--badge-bg)',
            borderRadius: 12,
            border: '1px solid var(--border-soft)',
          }}
        >
          {fileType === 'image' && file.preview ? (
            <img
              src={file.preview}
              alt="preview"
              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }}
            />
          ) : (
            <span style={{ fontSize: 22 }}>
              {fileType === 'audio' ? '🎵' : fileType === 'video' ? '🎬' : '📎'}
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text-strong)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {file.filename}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {fileType} · {formatBytes(file.size)}
            </div>
          </div>
          <button
            onClick={() => onFileSelect(null)}
            disabled={sending}
            style={{
              ...ITEM_BTN_BASE,
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'rgba(220,80,80,0.15)',
              color: '#a93232',
              fontSize: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            aria-label="Remover anexo"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div
          style={{
            fontSize: 12,
            color: '#a93232',
            background: 'rgba(220,80,80,0.10)',
            border: '1px solid rgba(220,80,80,0.30)',
            padding: '6px 10px',
            borderRadius: 8,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0] || null;
            onFileSelect(f);
            if (e.target) e.target.value = '';
          }}
          accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={sending}
          aria-label="Anexar arquivo"
          style={{
            ...ITEM_BTN_BASE,
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'var(--badge-bg)',
            color: 'var(--text-strong)',
            fontSize: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          📎
        </button>

        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={file ? 'Legenda (opcional)…' : 'Mensagem para o cliente…'}
          rows={1}
          style={{
            flex: 1,
            resize: 'none',
            padding: '10px 14px',
            borderRadius: 18,
            border: '1px solid var(--border-soft)',
            background: 'var(--cream)',
            color: 'var(--text-body)',
            fontFamily: 'inherit',
            fontSize: 14,
            lineHeight: 1.4,
            outline: 'none',
            maxHeight: 160,
            minHeight: 40,
            boxSizing: 'border-box',
          }}
        />

        <button
          onClick={onSend}
          disabled={!canSend}
          style={{
            ...ITEM_BTN_BASE,
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: canSend ? 'var(--gold)' : 'var(--badge-bg)',
            color: canSend ? 'var(--gold-ink)' : 'var(--text-soft)',
            fontSize: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: canSend ? 'pointer' : 'not-allowed',
            flexShrink: 0,
            boxShadow: canSend ? '0 2px 12px rgba(255,108,108,0.40)' : 'none',
            transition: 'background 0.15s',
          }}
          aria-label="Enviar mensagem"
        >
          {sending ? '…' : '➤'}
        </button>
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-fade)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ height: 14, width: '60%', background: 'var(--badge-bg)', borderRadius: 4 }} />
          <div style={{ height: 12, width: '40%', background: 'var(--badge-bg)', borderRadius: 4 }} />
          <div style={{ height: 12, width: '85%', background: 'var(--badge-bg)', borderRadius: 4 }} />
        </div>
      ))}
    </div>
  );
}

function EmptyThread() {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 12,
        padding: 32,
        color: 'var(--text-muted)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          background: 'var(--badge-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 32,
        }}
      >
        💬
      </div>
      <p style={{ margin: 0, fontSize: 15, maxWidth: 320 }}>
        Selecione uma conversa à esquerda para visualizar a thread completa.
      </p>
    </div>
  );
}
